import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CreditCard, ArrowClockwise, ArrowRight, X } from "@phosphor-icons/react";

const WINBACK_WINDOW_DAYS = 30;

export default function BillingControls() {
    const { user } = useAuth();
    const [opening, setOpening] = useState(false);

    if (!user) return null;
    // Lifetime members: nothing to manage (paid once, forever)
    if (user.lifetime_member) return null;
    if (!user.subscription_active && !user.cancelled_at) return null;

    const cancelledAt = user.cancelled_at ? new Date(user.cancelled_at) : null;
    const daysSinceCancel = cancelledAt
        ? Math.floor((Date.now() - cancelledAt.getTime()) / (1000 * 60 * 60 * 24))
        : null;
    const inWinBackWindow = daysSinceCancel !== null && daysSinceCancel <= WINBACK_WINDOW_DAYS;
    const daysLeft = inWinBackWindow ? Math.max(0, WINBACK_WINDOW_DAYS - daysSinceCancel) : 0;

    const openPortal = async () => {
        setOpening(true);
        try {
            const r = await api.post("/stripe/customer-portal");
            window.location.href = r.data.url;
        } catch (err) {
            const detail = err.response?.data?.detail || "Billing portal unavailable";
            toast.error(detail);
            setOpening(false);
        }
    };

    const reclaimSeat = async () => {
        // Reclaim = open a fresh checkout at the locked-in $20 rate
        setOpening(true);
        try {
            const r = await api.post("/stripe/create-checkout", {
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start reclaim checkout");
            setOpening(false);
        }
    };

    // Win-back banner (cancelled, within 30 days)
    if (inWinBackWindow && !user.subscription_active) {
        return (
            <section
                data-testid="winback-banner"
                className="bg-gradient-to-br from-[#1A1408] via-[#0A0A0A] to-[#0A0A0A] border border-yellow-400/40 relative overflow-hidden p-6 md:p-7"
            >
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-yellow-400/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-5">
                    <div className="flex-1">
                        <p className="label-mono text-yellow-300 mb-2 text-[10px]">★ FOUNDER WIN-BACK</p>
                        <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight mb-2">
                            Your seat will be released to the waitlist in{" "}
                            <span className="text-yellow-300">{daysLeft} days</span>.
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-2xl">
                            Reclaim it now and we'll re-activate your <span className="text-yellow-300 font-semibold">$20/mo locked-in rate</span> — no penalty, no negotiation.
                            After the {WINBACK_WINDOW_DAYS}-day window your seat goes back into the public pool at standard pricing.
                        </p>
                    </div>
                    <button
                        onClick={reclaimSeat}
                        disabled={opening}
                        data-testid="winback-reclaim-btn"
                        className="bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-bold px-6 py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_24px_rgba(250,204,21,0.5)] flex items-center gap-2 flex-shrink-0"
                    >
                        <ArrowClockwise size={16} weight="bold" />
                        {opening ? "OPENING..." : "RECLAIM MY SEAT"}
                        <ArrowRight size={14} weight="bold" />
                    </button>
                </div>
            </section>
        );
    }

    // Standard "Manage Billing" panel (active sub, no cancellation pending)
    return (
        <section data-testid="billing-controls" className="bg-[#0A0A0A] border border-white/10 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-electric-blue/10 border border-electric-blue/30 flex items-center justify-center">
                    <CreditCard size={18} weight="duotone" className="text-electric-blue" />
                </div>
                <div>
                    <p className="label-mono text-electric-blue mb-1">BILLING & SUBSCRIPTION</p>
                    <p className="text-sm text-muted-foreground">
                        Update your payment method, change plan, or cancel anytime.
                    </p>
                </div>
            </div>
            <button
                onClick={openPortal}
                disabled={opening}
                data-testid="billing-manage-btn"
                className="bg-transparent border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-5 py-2.5 text-xs label-mono transition-all disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
            >
                {opening ? "OPENING..." : "MANAGE BILLING"}
                <ArrowRight size={12} weight="bold" />
            </button>
        </section>
    );
}
